import { SubmitHandler, useForm } from "react-hook-form";
import {
  Box,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  IconButton,
  Input,
  Link,
  Radio,
  RadioGroup,
  Text,
  Textarea,
  Wrap,
} from "@chakra-ui/react";
import { DeleteIcon } from "@chakra-ui/icons";
import { TalesSpinner } from "../../../../atoms";
import { useDispatch, useSelector } from "react-redux";
import { AppThunkDispatch } from "../../../../../store";
import {
  getHighlightData,
  getHighlightError,
  isHighlightLoading,
} from "../../../../../store/highlight/selectors";
import { addHighlight } from "../../../../../store/highlight/actions/add";
import { toggleHighlightVisibility } from "../../../../../store/highlight/actions/toggleVisibility";
import { deleteHighlight } from "../../../../../store/highlight/actions/delete";
import useHightlight from "../../../../../utils/hooks/useHighlight";

interface HighlightInput {
  title: string;
  content: string;
  link: string;
  show: boolean;
  order: number;
}

export default function Highlight() {
  useHightlight();
  const dispatch = useDispatch<AppThunkDispatch>();
  const isPending = useSelector(isHighlightLoading);
  const highlights = useSelector(getHighlightData);
  const error = useSelector(getHighlightError);

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
    watch,
  } = useForm<HighlightInput>({
    defaultValues: {
      title: "",
      content: "",
      link: "",
      order: (highlights?.length ?? 0) + 1,
      show: true,
    },
  });

  const onSubmit: SubmitHandler<HighlightInput> = () => {
    const newHighlight = {
      title: getValues("title"),
      content: getValues("content"),
      link: getValues("link"),
      show: getValues("show"),
      order: getValues("order"),
    };
    dispatch(addHighlight(newHighlight));
  };

  return (
    <>
      {error && (
        <Box>
          <Text my="10px">{error}</Text>
        </Box>
      )}
      <Wrap>
        {highlights &&
          highlights.map((highlight) => (
            <Card key={highlight.id} w="md" m="10px">
              <CardHeader>
                <Heading size="base">{highlight.title}</Heading>
                {highlight.order && <Text>Ordine: {highlight.order}</Text>}
              </CardHeader>
              <CardBody>
                <Text whiteSpace="pre-line">{highlight.content}</Text>
                {highlight.link && (
                  <Link href={highlight.link}>{highlight.link}</Link>
                )}
              </CardBody>
              <CardFooter>
                <Button
                  aria-label={highlight.show ? "Nascondi" : "Mostra"}
                  variant="solid"
                  colorScheme={highlight.show ? "gray" : "green"}
                  onClick={() => dispatch(toggleHighlightVisibility(highlight))}
                  marginRight="20px"
                >
                  {highlight.show ? "NASCONDI DAL SITO" : "MOSTRA SUL SITO"}
                </Button>
                <IconButton
                  aria-label="Elimina"
                  variant="solid"
                  icon={<DeleteIcon />}
                  colorScheme="red"
                  onClick={() => dispatch(deleteHighlight(highlight))}
                />
              </CardFooter>
            </Card>
          ))}
        <Card w="md" m="10px">
          {isPending && (
            <CardBody>
              <Box w="30px" m="auto">
                <TalesSpinner />
              </Box>
            </CardBody>
          )}
          {!isPending && (
            <form onSubmit={handleSubmit(onSubmit)}>
              <CardHeader>
                <Heading size="base">Aggiungi nuovo</Heading>
              </CardHeader>
              <CardBody>
                <FormControl marginBottom="20px">
                  <FormLabel htmlFor="order">Ordine</FormLabel>
                  <Input id="order" type="number" {...register("order")} />
                  <FormErrorMessage>
                    {errors.title && errors.title.message}
                  </FormErrorMessage>
                </FormControl>
                <FormControl marginBottom="20px">
                  <FormLabel htmlFor="title">Titolo</FormLabel>
                  <Input id="title" type="text" {...register("title")} />
                  <FormErrorMessage>
                    {errors.title && errors.title.message}
                  </FormErrorMessage>
                </FormControl>
                <FormControl marginBottom="20px">
                  <FormLabel htmlFor="content">Contenuto</FormLabel>
                  <Textarea
                    whiteSpace="pre-wrap"
                    id="content"
                    {...register("content")}
                  />
                  <FormErrorMessage>
                    {errors.content && errors.content.message}
                  </FormErrorMessage>
                </FormControl>

                <FormControl marginBottom="20px">
                  <FormLabel htmlFor="title">Link</FormLabel>
                  <Input id="link" type="text" {...register("link")} />
                  <FormErrorMessage>
                    {errors.link && errors.link.message}
                  </FormErrorMessage>
                </FormControl>

                <FormControl>
                  <FormLabel>Mostrare sul sito</FormLabel>
                  <RadioGroup defaultValue={getValues("show").toString()}>
                    <Flex>
                      <Radio
                        value="true"
                        marginRight="30px"
                        {...register("show")}
                      >
                        Sì
                      </Radio>
                      <Radio value="false" {...register("show")}>
                        No
                      </Radio>
                    </Flex>
                  </RadioGroup>
                </FormControl>
              </CardBody>
              <CardFooter>
                <Button
                  type="submit"
                  isDisabled={
                    isPending ||
                    (watch("title") === "" &&
                      watch("content") === "" &&
                      watch("link") === "")
                  }
                  colorScheme="green"
                >
                  Crea
                </Button>
              </CardFooter>
            </form>
          )}
        </Card>
      </Wrap>
    </>
  );
}
